import React, { useEffect, useState } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { format } from "date-fns";
import fetchData from "../../functions/fetchData";

import Layout from "../../layout/Layout";
import Header from "../../components/Header";
import { getHeaders } from "../../utility/getHeader";
import { FaEdit, FaTrash, FaPlus, FaFileAlt, FaEye, FaCalculator } from "react-icons/fa";

export default function SupplierLedgerEntry() {
    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [supplierBalance, setSupplierBalance] = useState(0);
    const [customerBalance, setCustomerBalance] = useState(0);
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState({
        transaction_id: "",
        date: format(new Date(), "yyyy-MM-dd"),
        account_type: "Account Payable",
        supplier_id: "",
        customer_id: "",
        description: "",
        amount: "",
        entry_type: "credit", // 'credit' for supplier payments, 'debit' for customer receipts
        reference_id: "",
    });

    // Filters
    const [filters, setFilters] = useState({
        supplier_id: "",
        start_date: "",
        end_date: "",
        entry_type: ""
    });

    useEffect(() => {
        const fetchSuppliers = async () => {
            try {
                console.log("Fetching suppliers...");
                fetchData("suppliers", setSuppliers, "id", {});
            } catch (err) {
                console.error("Error fetching suppliers:", err);
                toast.error("Failed to load suppliers.");
                setSuppliers([]);
            }
        };
        
        const fetchCustomers = async () => {
            try {
                console.log("Fetching customers...");
                fetchData("customers", setCustomers, "id", {});
            } catch (err) {
                console.error("Error fetching customers:", err);
                toast.error("Failed to load customers.");
                setCustomers([]);
            }
        };
        
        console.log("Component mounted, fetching data...");
        fetchSuppliers();
        fetchCustomers();
        fetchLedgerEntries();
    }, []);

    const fetchLedgerEntries = async () => {
        try {
            console.log("Fetching ledger entries from both tables...");
            
            // Fetch supplier ledger entries
            const supplierEntries = await new Promise((resolve) => {
                const tempData = [];
                fetchData("supplier_ledger_entries", (data) => {
                    data.forEach(entry => {
                        tempData.push({
                            ...entry,
                            source_table: 'supplier_ledger_entries',
                            entry_category: entry.credit_amount > 0 ? 'credit' : 'debit'
                        });
                    });
                    resolve(tempData);
                }, "id", {});
            });
            
            // Fetch general ledger entries
            const generalEntries = await new Promise((resolve) => {
                const tempData = [];
                fetchData("ledger_entries", (data) => {
                    data.forEach(entry => {
                        // Only include customer-related entries (Account Receivable)
                        if (entry.account_type === 'Account Recievable') {
                            tempData.push({
                                ...entry,
                                source_table: 'ledger_entries',
                                entry_category: entry.debit_amount > 0 ? 'debit' : 'credit',
                                customer_id: entry.account_id
                            });
                        }
                    });
                    resolve(tempData);
                }, "id", {});
            });
            
            // Combine both datasets
            const combinedData = [...supplierEntries, ...generalEntries];
            
            // Sort by date (newest first)
            combinedData.sort((a, b) => new Date(b.date) - new Date(a.date));
            
            setData(combinedData);
            setFilteredData(combinedData);
            
            console.log("Combined ledger entries:", combinedData);
            
        } catch (error) {
            console.error("Error fetching ledger entries:", error);
            setData([]);
            setFilteredData([]);
            toast.error("Failed to fetch ledger entries. Please check your connection.");
        }
    };

    const calculateSupplierBalance = async (supplierId) => {
        try {
            console.log("Calculating balance for supplier:", supplierId);
            const response = await axios.get(`/supplier-ledger-balance/${supplierId}`, getHeaders());
            console.log("Balance response:", response.data);
            
            if (response.data.success) {
                setSupplierBalance(response.data.balance || 0);
            } else {
                console.error("Balance API returned success: false", response.data);
                setSupplierBalance(0);
            }
        } catch (error) {
            console.error("Error calculating balance:", error.response?.data || error.message);
            setSupplierBalance(0);
        }
    };

    const calculateCustomerBalance = async (customerId) => {
        try {
            console.log("Calculating balance for customer:", customerId);
            // You'll need to implement this endpoint on backend
            const response = await axios.get(`/customer-ledger-balance/${customerId}`, getHeaders());
            console.log("Customer balance response:", response.data);
            
            if (response.data.success) {
                setCustomerBalance(response.data.balance || 0);
            } else {
                console.error("Customer balance API returned success: false", response.data);
                setCustomerBalance(0);
            }
        } catch (error) {
            console.error("Error calculating customer balance:", error.response?.data || error.message);
            setCustomerBalance(0);
        }
    };

    const handleSupplierChange = (supplierId) => {
        setFormData(prev => ({ ...prev, supplier_id: supplierId }));
        if (supplierId) {
            const supplier = suppliers.find(s => s.id === parseInt(supplierId));
            setSelectedSupplier(supplier);
            calculateSupplierBalance(supplierId);
        } else {
            setSelectedSupplier(null);
            setSupplierBalance(0);
        }
    };

    const handleCustomerChange = (customerId) => {
        setFormData(prev => ({ ...prev, customer_id: customerId }));
        if (customerId) {
            const customer = customers.find(c => c.id === parseInt(customerId));
            setSelectedCustomer(customer);
            calculateCustomerBalance(customerId);
        } else {
            setSelectedCustomer(null);
            setCustomerBalance(0);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name === "supplier_id") {
            handleSupplierChange(value);
        } else if (name === "customer_id") {
            handleCustomerChange(value);
        } else if (name === "entry_type") {
            // Reset selections when entry type changes
            setFormData(prev => ({ 
                ...prev, 
                [name]: value,
                supplier_id: "",
                customer_id: ""
            }));
            setSelectedSupplier(null);
            setSelectedCustomer(null);
            setSupplierBalance(0);
            setCustomerBalance(0);
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const transactionId = formData.transaction_id || `TXN_${Date.now()}`;
        const amount = parseFloat(formData.amount);

        try {
            if (formData.entry_type === 'credit') {
                // Credit entry: Payment to supplier -> save to supplier_ledger_entries
                const supplierLedgerData = {
                    transaction_id: transactionId,
                    date: formData.date,
                    account_type: "Account Payable",
                    supplier_id: parseInt(formData.supplier_id),
                    description: formData.description,
                    debit_amount: 0.00,
                    credit_amount: amount, // Credit to reduce supplier balance
                    reference_id: formData.reference_id || null,
                };
                
                const response = await axios.post("/supplier-ledger-entry", supplierLedgerData, getHeaders());
                
                if (response.data.success) {
                    toast.success("Supplier payment recorded successfully!");
                } else {
                    throw new Error(response.data.message || "Failed to save supplier payment");
                }
                
            } else if (formData.entry_type === 'debit') {
                // Debit entry: Receipt from customer -> save to customer ledger
                const customerLedgerData = {
                    customer_id: parseInt(formData.customer_id),
                    description: formData.description,
                    debit_amount: amount, // Debit customer account
                    credit_amount: 0.00,
                    account_type: "Account Receivable",
                    reference_id: formData.reference_id || null,
                    date: formData.date,
                };
                
                const response = await axios.post("/customer-ledger-entry", customerLedgerData, getHeaders());
                
                if (response.data.success) {
                    toast.success("Customer receipt recorded successfully!");
                } else {
                    throw new Error(response.data.message || "Failed to save customer receipt");
                }
            }

            resetForm();
            fetchLedgerEntries();
            
            // Update balance based on entry type
            if (formData.entry_type === 'credit' && formData.supplier_id) {
                calculateSupplierBalance(formData.supplier_id);
            } else if (formData.entry_type === 'debit' && formData.customer_id) {
                calculateCustomerBalance(formData.customer_id);
            }
            
        } catch (error) {
            console.error(error);
            toast.error("Failed to save entry.");
        }
    };

    const resetForm = () => {
        setFormData({
            transaction_id: "",
            date: format(new Date(), "yyyy-MM-dd"),
            account_type: "Account Payable",
            supplier_id: "",
            customer_id: "",
            description: "",
            amount: "",
            entry_type: "credit",
            reference_id: "",
        });
        setEditingItem(null);
        setSelectedSupplier(null);
        setSelectedCustomer(null);
        setSupplierBalance(0);
        setCustomerBalance(0);
    };

    const handleEdit = (item) => {
        setEditingItem(item);
        setFormData({
            transaction_id: item.transaction_id,
            date: format(new Date(item.date), "yyyy-MM-dd"),
            account_type: item.account_type,
            supplier_id: item.supplier_id.toString(),
            description: item.description,
            amount: item.debit_amount > 0 ? item.debit_amount : item.credit_amount,
            entry_type: item.debit_amount > 0 ? "debit" : "credit",
            reference_id: item.reference_id || "",
        });
        handleSupplierChange(item.supplier_id.toString());
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this entry?")) {
            try {
                await axios.delete(`/supplier_ledger_entries/${id}`, getHeaders());
                toast.success("Entry deleted successfully!");
                fetchLedgerEntries();
            } catch (error) {
                console.error(error);
                toast.error("Failed to delete entry.");
            }
        }
    };

    const applyFilters = () => {
        let filtered = data;

        if (filters.supplier_id) {
            filtered = filtered.filter(item => 
                (item.source_table === 'supplier_ledger_entries' && item.supplier_id === parseInt(filters.supplier_id)) ||
                (item.source_table === 'ledger_entries' && item.customer_id === parseInt(filters.supplier_id))
            );
        }
        
        if (filters.start_date) {
            filtered = filtered.filter(item => new Date(item.date) >= new Date(filters.start_date));
        }
        
        if (filters.end_date) {
            filtered = filtered.filter(item => new Date(item.date) <= new Date(filters.end_date));
        }
        
        if (filters.entry_type) {
            filtered = filtered.filter(item => {
                if (filters.entry_type === 'debit') {
                    return item.entry_category === 'debit';
                } else if (filters.entry_type === 'credit') {
                    return item.entry_category === 'credit';
                }
                return true;
            });
        }

        setFilteredData(filtered);
    };

    const resetFilters = () => {
        setFilters({
            supplier_id: "",
            start_date: "",
            end_date: "",
            entry_type: ""
        });
        setFilteredData(data);
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount) + ' THB';
    };

    return (
        <Layout>
            <Header title="Supplier Ledger Entry" />
            <ToastContainer />
            
            {/* Compact Entry Form */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card border-primary">
                        <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
                            <h6 className="mb-0">
                                <FaPlus className="me-2" />
                                {editingItem ? 'Edit Entry' : 'New Entry'}
                            </h6>
                            {(selectedSupplier || selectedCustomer) && (
                                <div className="d-flex align-items-center">
                                    <FaCalculator className="me-2" />
                                    <span>
                                        {formData.entry_type === 'credit' ? 'Supplier' : 'Customer'} Balance: 
                                        <strong>
                                            {formatCurrency(formData.entry_type === 'credit' ? supplierBalance : customerBalance)}
                                        </strong>
                                    </span>
                                </div>
                            )}
                        </div>
                        <div className="card-body p-3">
                            <form onSubmit={handleSubmit}>
                                <div className="row g-2">
                                    <div className="col-md-2">
                                        <label className="form-label small">Date</label>
                                        <input
                                            type="date"
                                            className="form-control form-control-sm"
                                            name="date"
                                            value={formData.date}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                    <div className="col-md-2">
                                        <label className="form-label small">Entry Type</label>
                                        <select
                                            className="form-control form-control-sm"
                                            name="entry_type"
                                            value={formData.entry_type}
                                            onChange={handleInputChange}
                                            required
                                        >
                                            <option value="credit">Supplier Payment</option>
                                            <option value="debit">Customer Receipt</option>
                                        </select>
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label small">
                                            {formData.entry_type === 'credit' ? 'Supplier' : 'Customer'}
                                        </label>
                                        {formData.entry_type === 'credit' ? (
                                            <select
                                                className="form-control form-control-sm"
                                                name="supplier_id"
                                                value={formData.supplier_id}
                                                onChange={handleInputChange}
                                                required
                                            >
                                                <option value="">Select Supplier</option>
                                                {suppliers.map((supplier) => (
                                                    <option key={supplier.id} value={supplier.id}>
                                                        {supplier.supplier_name}
                                                    </option>
                                                ))}
                                            </select>
                                        ) : (
                                            <select
                                                className="form-control form-control-sm"
                                                name="customer_id"
                                                value={formData.customer_id}
                                                onChange={handleInputChange}
                                                required
                                            >
                                                <option value="">Select Customer</option>
                                                {customers.map((customer) => (
                                                    <option key={customer.id} value={customer.id}>
                                                        {customer.name}
                                                    </option>
                                                ))}
                                            </select>
                                        )}
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label small">Description</label>
                                        <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            name="description"
                                            value={formData.description}
                                            onChange={handleInputChange}
                                            placeholder="Transaction description"
                                            required
                                        />
                                    </div>
                                    <div className="col-md-2">
                                        <label className="form-label small">Amount</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            className="form-control form-control-sm"
                                            name="amount"
                                            value={formData.amount}
                                            onChange={handleInputChange}
                                            placeholder="0.00"
                                            required
                                        />
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label small">Reference ID</label>
                                        <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            name="reference_id"
                                            value={formData.reference_id}
                                            onChange={handleInputChange}
                                            placeholder="Optional reference"
                                        />
                                    </div>
                                    <div className="col-md-3 mt-3 d-flex align-items-end">
                                         <label className="form-label small">   .</label>
                                        <button type="submit" className="btn btn-primary btn-sm me-2">
                                            {editingItem ? 'Update' : 'Save'} {formData.entry_type === 'credit' ? 'Payment' : 'Receipt'}
                                        </button>
                                        {editingItem && (
                                            <button 
                                                type="button" 
                                                className="btn btn-secondary btn-sm"
                                                onClick={resetForm}
                                            >
                                                Cancel
                                            </button>
                                        )}
                                    </div>
                                </div>
                                
                            </form>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="row mb-3">
                <div className="col-12">
                    <div className="card border-secondary">
                        <div className="card-header bg-light">
                            <h6 className="mb-0">
                                <FaFileAlt className="me-2" />
                                Filters & Reports
                            </h6>
                        </div>
                        <div className="card-body p-3">
                            <div className="row g-2">
                                <div className="col-md-3">
                                    <label className="form-label small">Filter by Supplier/Customer</label>
                                    <select
                                        className="form-control form-control-sm"
                                        value={filters.supplier_id}
                                        onChange={(e) => setFilters(prev => ({...prev, supplier_id: e.target.value}))}
                                    >
                                        <option value="">All Suppliers & Customers</option>
                                        <optgroup label="Suppliers">
                                            {suppliers.map((supplier) => (
                                                <option key={`sup_${supplier.id}`} value={supplier.id}>
                                                    {supplier.supplier_name}
                                                </option>
                                            ))}
                                        </optgroup>
                                        <optgroup label="Customers">
                                            {customers.map((customer) => (
                                                <option key={`cust_${customer.id}`} value={customer.id}>
                                                    {customer.name}
                                                </option>
                                            ))}
                                        </optgroup>
                                    </select>
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label small">Start Date</label>
                                    <input
                                        type="date"
                                        className="form-control form-control-sm"
                                        value={filters.start_date}
                                        onChange={(e) => setFilters(prev => ({...prev, start_date: e.target.value}))}
                                    />
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label small">End Date</label>
                                    <input
                                        type="date"
                                        className="form-control form-control-sm"
                                        value={filters.end_date}
                                        onChange={(e) => setFilters(prev => ({...prev, end_date: e.target.value}))}
                                    />
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label small">Entry Type</label>
                                    <select
                                        className="form-control form-control-sm"
                                        value={filters.entry_type}
                                        onChange={(e) => setFilters(prev => ({...prev, entry_type: e.target.value}))}
                                    >
                                        <option value="">All Types</option>
                                        <option value="debit">Expenses</option>
                                        <option value="credit">Payments</option>
                                    </select>
                                </div>
                                <div className="col-md-3 d-flex align-items-end">
                                    <label className="form-label small">.</label>
                                    <button 
                                        className="btn btn-primary btn-sm me-2"
                                        onClick={applyFilters}
                                    >
                                        Apply Filters
                                    </button>
                                    <button 
                                        className="btn btn-secondary btn-sm"
                                        onClick={resetFilters}
                                    >
                                        Reset
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Custom Reports Table */}
            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-header bg-info text-white">
                            <h6 className="mb-0">
                                <FaEye className="me-2" />
                                Ledger Entries ({filteredData.length} records)
                            </h6>
                        </div>
                        <div className="card-body p-0">
                            {filteredData.length === 0 ? (
                                <div className="text-center py-4">
                                    <p className="text-muted mb-0">No ledger entries found.</p>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-striped table-hover mb-0">
                                        <thead className="table-dark">
                                            <tr>
                                                <th className="py-2 px-3">Date</th>
                                                <th className="py-2 px-3">Transaction ID</th>
                                                <th className="py-2 px-3">Supplier/Customer</th>
                                                <th className="py-2 px-3">Description</th>
                                                <th className="py-2 px-3">Type</th>
                                                <th className="py-2 px-3 text-end">Debit</th>
                                                <th className="py-2 px-3 text-end">Credit</th>
                                                <th className="py-2 px-3 text-center">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredData.map((entry) => {
                                                // Find supplier or customer based on entry source
                                                const supplier = entry.source_table === 'supplier_ledger_entries' 
                                                    ? suppliers.find(s => s.id === entry.supplier_id)
                                                    : null;
                                                const customer = entry.source_table === 'ledger_entries'
                                                    ? customers.find(c => c.id === entry.customer_id)
                                                    : null;
                                                
                                                return (
                                                    <tr key={`${entry.source_table}_${entry.id}`}>
                                                        <td className="py-2 px-3 small">
                                                            {format(new Date(entry.date), "MMM dd, yyyy")}
                                                        </td>
                                                        <td className="py-2 px-3 small">
                                                            <span className="badge bg-secondary">
                                                                {entry.transaction_id}
                                                            </span>
                                                        </td>
                                                        <td className="py-2 px-3 small">
                                                            <strong>
                                                                {supplier ? supplier.supplier_name : 
                                                                 customer ? customer.name :
                                                                 'Unknown'}
                                                            </strong>
                                                            <br />
                                                            <small className="text-muted">
                                                                {entry.source_table === 'supplier_ledger_entries' ? 'Supplier' : 'Customer'}
                                                            </small>
                                                        </td>
                                                        <td className="py-2 px-3 small">
                                                            {entry.description}
                                                        </td>
                                                        <td className="py-2 px-3">
                                                            <span className={`badge ${
                                                                entry.entry_category === 'debit' ? 'bg-info' : 'bg-success'
                                                            }`}>
                                                                {entry.entry_category === 'debit' 
                                                                    ? (entry.source_table === 'supplier_ledger_entries' ? 'Supplier Expense' : 'Customer Receipt')
                                                                    : 'Supplier Payment'
                                                                }
                                                            </span>
                                                        </td>
                                                        <td className="py-2 px-3 text-end small">
                                                            {entry.debit_amount > 0 && (
                                                                <span className="text-danger fw-bold">
                                                                    {formatCurrency(entry.debit_amount)}
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="py-2 px-3 text-end small">
                                                            {entry.credit_amount > 0 && (
                                                                <span className="text-success fw-bold">
                                                                    {formatCurrency(entry.credit_amount)}
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="py-2 px-3 text-center">
                                                            {entry.source_table === 'supplier_ledger_entries' && (
                                                                <>
                                                                    <button
                                                                        className="btn btn-outline-primary btn-sm me-1"
                                                                        onClick={() => handleEdit(entry)}
                                                                        title="Edit Entry"
                                                                    >
                                                                        <FaEdit />
                                                                    </button>
                                                                    <button
                                                                        className="btn btn-outline-danger btn-sm"
                                                                        onClick={() => handleDelete(entry.id)}
                                                                        title="Delete Entry"
                                                                    >
                                                                        <FaTrash />
                                                                    </button>
                                                                </>
                                                            )}
                                                            {entry.source_table === 'ledger_entries' && (
                                                                <span className="badge bg-light text-dark">Read Only</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                        <tfoot className="table-light">
                                            <tr>
                                                <td colSpan="5" className="py-2 px-3 text-end fw-bold">Totals:</td>
                                                <td className="py-2 px-3 text-end fw-bold text-danger">
                                                    {formatCurrency(filteredData.reduce((sum, entry) => sum + parseFloat(entry.debit_amount || 0), 0))}
                                                </td>
                                                <td className="py-2 px-3 text-end fw-bold text-success">
                                                    {formatCurrency(filteredData.reduce((sum, entry) => sum + parseFloat(entry.credit_amount || 0), 0))}
                                                </td>
                                                <td className="py-2 px-3"></td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
}
